import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  OnDestroy,
  signal,
  Type,
  viewChild,
} from '@angular/core';
import { Editor as TipTapEditor } from '@tiptap/core';
import {
  BoldIconComponent,
  ItalicIconComponent,
  UnderlineIconComponent,
  BulletListIconComponent,
  OrderedListIconComponent,
  AlignLeftIconComponent,
  AlignRightIconComponent,
  AlignCenterIconComponent,
  JustifyIconComponent,
  TableIconComponent,
  TableAddColumnBeforeIconComponent,
  TableAddColumnAfterIconComponent,
  TableDeleteColumnIconComponent,
  TableAddRowBeforeIconComponent,
  TableAddRowAfterIconComponent,
  TableDeleteRowIconComponent,
  TableDeleteIconComponent,
  TableMergeCellsIconComponent,
  TableSplitCellIconComponent,
  TableHeaderRowIconComponent,
  TableHeaderColumnIconComponent,
  TableHeaderCellIconComponent,
  TableSettingsIconComponent,
  LinkIconComponent,
} from '@kuiper/icons';
import { NgComponentOutlet } from '@angular/common';
import {
  TableGridSelectorComponent,
  TableSize,
} from '../table-grid-selector/table-grid-selector.component';
import {
  LinkInputComponent,
  LinkData,
} from '../link-input/link-input.component';
import tippy, { Instance, Props } from 'tippy.js';
import { TranslocoService } from '@jsverse/transloco';
import { delay, of } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

interface ToolbarButton {
  title: string;
  name: string;
  action: () => void;
  isActive: () => boolean;
  hasSeparator: boolean;
  iconComponent: Type<unknown>;
}

interface TableAction {
  title: string;
  name: string;
  action: () => boolean;
  iconComponent: Type<unknown>;
  isDisabled?: () => boolean;
}

@Component({
  selector: 'lib-tiptap-editor-menu',
  imports: [NgComponentOutlet, TableGridSelectorComponent, LinkInputComponent],
  templateUrl: './tiptap-editor-menu.component.html',
  styleUrl: './tiptap-editor-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TiptapEditorMenuComponent implements OnDestroy {
  private readonly elementRef = inject(ElementRef);
  private readonly transloco = inject(TranslocoService);
  private readonly destroyRef = inject(DestroyRef);

  tableGridContent = viewChild<ElementRef<HTMLDivElement>>('tableGridContent');
  tableActionsContent = viewChild<ElementRef<HTMLDivElement>>(
    'tableActionsContent'
  );
  linkInputContent = viewChild<ElementRef<HTMLDivElement>>('linkInputContent');
  linkInputComponent = viewChild<LinkInputComponent>('linkInputComponent');

  editor = input.required<TipTapEditor>();
  maxLength = input<number>(6000);
  characterCount = input<number>(0);
  disabled = input<boolean>(false);

  private tableGridTippy: Instance<Props> | null = null;
  protected tableActionsTippy: Instance<Props> | null = null;
  private linkInputTippy: Instance<Props> | null = null;

  isTableGridTippyVisible = signal(!!this.tableGridTippy?.state.isVisible);
  isTableActionsTippyVisible = signal(
    !!this.tableActionsTippy?.state.isVisible
  );
  isLinkInputTippyVisible = signal(false);

  constructor() {
    afterNextRender(() => {
      this.initTableGridTippy();
      this.initTableActionsTippy();
      this.initLinkInputTippy();
    });
  }

  toolbarButtons = computed<ToolbarButton[]>(() => {
    const buttons: ToolbarButton[] = [
      {
        title: this.transloco.translate('rich_text_toolbar_bold'),
        name: 'bold',
        action: () => this.editor().chain().focus().toggleBold().run(),
        isActive: () => this.editor().isActive('bold'),
        hasSeparator: false,
        iconComponent: BoldIconComponent,
      },
      {
        title: this.transloco.translate('rich_text_toolbar_italic'),
        name: 'italic',
        action: () => this.editor().chain().focus().toggleItalic().run(),
        isActive: () => this.editor().isActive('italic'),
        hasSeparator: false,
        iconComponent: ItalicIconComponent,
      },
      {
        title: this.transloco.translate('rich_text_toolbar_underline'),
        name: 'underline',
        action: () => this.editor().chain().focus().toggleUnderline().run(),
        isActive: () => this.editor().isActive('underline'),
        hasSeparator: true,
        iconComponent: UnderlineIconComponent,
      },
      {
        title: this.transloco.translate('rich_text_toolbar_align_left'),
        name: 'alignLeft',
        action: () => this.editor().chain().focus().setTextAlign('left').run(),
        isActive: () => this.editor().isActive({ textAlign: 'left' }),
        hasSeparator: false,
        iconComponent: AlignLeftIconComponent,
      },
      {
        title: this.transloco.translate('rich_text_toolbar_align_center'),
        name: 'alignCenter',
        action: () =>
          this.editor().chain().focus().setTextAlign('center').run(),
        isActive: () => this.editor().isActive({ textAlign: 'center' }),
        hasSeparator: false,
        iconComponent: AlignCenterIconComponent,
      },
      {
        title: this.transloco.translate('rich_text_toolbar_align_right'),
        name: 'alignRight',
        action: () => this.editor().chain().focus().setTextAlign('right').run(),
        isActive: () => this.editor().isActive({ textAlign: 'right' }),
        hasSeparator: false,
        iconComponent: AlignRightIconComponent,
      },
      {
        title: this.transloco.translate('rich_text_toolbar_justify'),
        name: 'justify',
        action: () =>
          this.editor().chain().focus().setTextAlign('justify').run(),
        isActive: () => this.editor().isActive({ textAlign: 'justify' }),
        hasSeparator: true,
        iconComponent: JustifyIconComponent,
      },
      {
        title: this.transloco.translate('rich_text_toolbar_bullet_list'),
        name: 'bulletList',
        action: () => this.editor().chain().focus().toggleBulletList().run(),
        isActive: () => this.editor().isActive('bulletList'),
        hasSeparator: false,
        iconComponent: BulletListIconComponent,
      },
      {
        title: this.transloco.translate('rich_text_toolbar_ordered_list'),
        name: 'orderedList',
        action: () => this.editor().chain().focus().toggleOrderedList().run(),
        isActive: () => this.editor().isActive('orderedList'),
        hasSeparator: true,
        iconComponent: OrderedListIconComponent,
      },
    ];

    buttons.push(
      {
        title: this.transloco.translate('rich_text_toolbar_insert_table'),
        name: 'insertTable',
        action: () => this.tableGridTippy?.show(),
        isActive: () => this.isTableGridTippyVisible(),
        hasSeparator: false,
        iconComponent: TableIconComponent,
      },
      {
        title: this.transloco.translate('rich_text_toolbar_table_actions'),
        name: 'tableActions',
        action: () => this.tableActionsTippy?.show(),
        isActive: () => this.isTableActionsTippyVisible(),
        hasSeparator: true,
        iconComponent: TableSettingsIconComponent,
      },
      {
        title: this.transloco.translate('rich_text_toolbar_set_link'),
        name: 'setLink',
        action: () => this.showLinkInput(),
        isActive: () =>
          this.editor().isActive('link') || this.isLinkInputTippyVisible(),
        hasSeparator: false,
        iconComponent: LinkIconComponent,
      }
    );

    return buttons;
  });

  tableActions = computed<TableAction[]>(() => [
    {
      title: this.transloco.translate('rich_text_table_add_column_before'),
      name: 'addColumnBefore',
      action: () => this.editor().chain().focus().addColumnBefore().run(),
      iconComponent: TableAddColumnBeforeIconComponent,
      isDisabled: () => !this.isInTable(),
    },
    {
      title: this.transloco.translate('rich_text_table_add_column_after'),
      name: 'addColumnAfter',
      action: () => this.editor().chain().focus().addColumnAfter().run(),
      iconComponent: TableAddColumnAfterIconComponent,
      isDisabled: () => !this.isInTable(),
    },
    {
      title: this.transloco.translate('rich_text_table_delete_column'),
      name: 'deleteColumn',
      action: () => this.editor().chain().focus().deleteColumn().run(),
      iconComponent: TableDeleteColumnIconComponent,
      isDisabled: () => !this.isInTable(),
    },
    {
      title: this.transloco.translate('rich_text_table_add_row_before'),
      name: 'addRowBefore',
      action: () => this.editor().chain().focus().addRowBefore().run(),
      iconComponent: TableAddRowBeforeIconComponent,
      isDisabled: () => !this.isInTable(),
    },
    {
      title: this.transloco.translate('rich_text_table_add_row_after'),
      name: 'addRowAfter',
      action: () => this.editor().chain().focus().addRowAfter().run(),
      iconComponent: TableAddRowAfterIconComponent,
      isDisabled: () => !this.isInTable(),
    },
    {
      title: this.transloco.translate('rich_text_table_delete_row'),
      name: 'deleteRow',
      action: () => this.editor().chain().focus().deleteRow().run(),
      iconComponent: TableDeleteRowIconComponent,
      isDisabled: () => !this.isInTable(),
    },
    {
      title: this.transloco.translate('rich_text_table_merge_cells'),
      name: 'mergeCells',
      action: () => this.editor().chain().focus().mergeCells().run(),
      iconComponent: TableMergeCellsIconComponent,
      isDisabled: () => !this.isInTable(),
    },
    {
      title: this.transloco.translate('rich_text_table_split_cell'),
      name: 'splitCell',
      action: () => this.editor().chain().focus().splitCell().run(),
      iconComponent: TableSplitCellIconComponent,
      isDisabled: () => !this.isInTable(),
    },
    {
      title: this.transloco.translate('rich_text_table_toggle_header_row'),
      name: 'toggleHeaderRow',
      action: () => this.editor().chain().focus().toggleHeaderRow().run(),
      iconComponent: TableHeaderRowIconComponent,
      isDisabled: () => !this.isInTable(),
    },
    {
      title: this.transloco.translate('rich_text_table_toggle_header_column'),
      name: 'toggleHeaderColumn',
      action: () => this.editor().chain().focus().toggleHeaderColumn().run(),
      iconComponent: TableHeaderColumnIconComponent,
      isDisabled: () => !this.isInTable(),
    },
    {
      title: this.transloco.translate('rich_text_table_toggle_header_cell'),
      name: 'toggleHeaderCell',
      action: () => this.editor().chain().focus().toggleHeaderCell().run(),
      iconComponent: TableHeaderCellIconComponent,
      isDisabled: () => !this.isInTable(),
    },
    {
      title: this.transloco.translate('rich_text_table_delete_table'),
      name: 'deleteTable',
      action: () => this.editor().chain().focus().deleteTable().run(),
      iconComponent: TableDeleteIconComponent,
      isDisabled: () => !this.isInTable(),
    },
  ]);

  closeDropdowns(): void {
    this.tableGridTippy?.hide();
    this.tableActionsTippy?.hide();
    this.linkInputTippy?.hide();
  }

  private initTableGridTippy(): void {
    const btn = this.elementRef.nativeElement.querySelector(
      'button[name="insertTable"]'
    ) as HTMLButtonElement;
    const content = this.tableGridContent();
    if (btn && content) {
      content.nativeElement.style.display = '';
      this.tableGridTippy = tippy(btn, {
        content: content.nativeElement,
        interactive: true,
        trigger: 'manual',
        placement: 'top-start',
        appendTo: () => document.body,
        onShow: () => {
          this.isTableGridTippyVisible.set(true);
        },
        onHide: () => {
          this.isTableGridTippyVisible.set(false);
        },
        onMount: instance => {
          const tippyBox = instance.popper.querySelector(
            '.tippy-box'
          ) as HTMLElement;
          if (tippyBox) {
            tippyBox.dataset['menuTippy'] = 'true';
          }
        },
      });
    }
  }

  private initTableActionsTippy(): void {
    const btn = this.elementRef.nativeElement.querySelector(
      'button[name="tableActions"]'
    ) as HTMLButtonElement;
    const content = this.tableActionsContent();
    if (btn && content) {
      content.nativeElement.style.display = '';
      this.tableActionsTippy = tippy(btn, {
        content: content.nativeElement,
        interactive: true,
        trigger: 'manual',
        placement: 'top-start',
        appendTo: () => document.body,
        onShow: () => {
          this.isTableActionsTippyVisible.set(true);
        },
        onHide: () => {
          this.isTableActionsTippyVisible.set(false);
        },
        onMount: instance => {
          const tippyBox = instance.popper.querySelector(
            '.tippy-box'
          ) as HTMLElement;
          if (tippyBox) {
            tippyBox.dataset['menuTippy'] = 'true';
          }
        },
      });
    }
  }

  insertTable(size: TableSize): void {
    this.editor()
      .chain()
      .focus()
      .insertTable({ rows: size.rows, cols: size.cols, withHeaderRow: true })
      .run();
    this.closeDropdowns();
  }

  isInTable(): boolean {
    return this.editor().isActive('table');
  }

  private initLinkInputTippy(): void {
    const btn = this.elementRef.nativeElement.querySelector(
      'button[name="setLink"]'
    ) as HTMLButtonElement;
    const content = this.linkInputContent();
    if (btn && content) {
      content.nativeElement.style.display = '';
      this.linkInputTippy = tippy(btn, {
        content: content.nativeElement,
        interactive: true,
        trigger: 'manual',
        placement: 'top-start',
        appendTo: () => document.body,
        onShow: () => {
          this.isLinkInputTippyVisible.set(true);
          const { state } = this.editor();
          const { from, to } = state.selection;
          const selectedText = state.doc.textBetween(from, to, '');
          const attrs = this.editor().getAttributes('link') as {
            href?: string;
          };
          this.linkInputComponent()?.setInitialValues(
            selectedText || '',
            attrs.href || ''
          );
          of(null)
            .pipe(takeUntilDestroyed(this.destroyRef), delay(0))
            .subscribe(() => this.linkInputComponent()?.focusDisplayName());
        },
        onHide: () => {
          this.isLinkInputTippyVisible.set(false);
        },
        onMount: instance => {
          const tippyBox = instance.popper.querySelector(
            '.tippy-box'
          ) as HTMLElement;
          if (tippyBox) {
            tippyBox.dataset['menuTippy'] = 'true';
          }
        },
      });
    }
  }

  showLinkInput(): void {
    this.linkInputTippy?.show();
  }

  onLinkInsert(linkData: LinkData): void {
    const { displayName, url } = linkData;
    const { state } = this.editor();
    const { from, to } = state.selection;
    const hasSelection = from !== to;

    if (hasSelection) {
      this.editor()
        .chain()
        .focus()
        .deleteSelection()
        .insertContent(`<a href="${url}">${displayName}</a>`)
        .run();
    } else {
      this.editor()
        .chain()
        .focus()
        .insertContent(`<a href="${url}">${displayName}</a>`)
        .run();
    }
    this.linkInputTippy?.hide();
  }

  onLinkCancel(): void {
    this.linkInputTippy?.hide();
  }

  ngOnDestroy(): void {
    this.tableGridTippy?.destroy();
    this.tableActionsTippy?.destroy();
    this.linkInputTippy?.destroy();
  }
}
